import { Accommodation } from 'src/app/trip-calculator/models/accommodation';
import {
  CarSubtype,
  FlightStopType,
  Transport,
  TransportMode,
} from 'src/app/trip-calculator/models/transport';
import { Trip } from 'src/app/trip-calculator/models/trip';
import {
  DirectFlightTipData,
  EcoAccommodationTipData,
  ReductionTip,
  TransportAlternativeTipData,
} from '../models/reduction-tip';
import {
  AlternativeMode,
  getBestAlternativeMode,
} from './reduction-tip-savings';

/**
 * Passengers per car assumed by the carpooling alternative. Mirrors
 * `CARPOOLING_PASSENGERS` in the API's `reduction_tips_service`: both must
 * stay in step, otherwise the applied trip does not land on the emissions the
 * dialog promised.
 */
const CARPOOLING_PASSENGERS = 2;

/**
 * How each alternative rewrites the transport, mirroring the mocks the API
 * priced the tip with.
 */
const ALTERNATIVE_TRANSPORT: Record<AlternativeMode, Partial<Transport>> = {
  train: { type: TransportMode.LongDistanceTrain },
  bus: { type: TransportMode.CoachBus, isPrivate: false },
  carpooling: {
    type: TransportMode.CombustionCar,
    subtype: CarSubtype.Medium,
    passengerPerVehicle: CARPOOLING_PASSENGERS,
  },
};

/**
 * The trip as it would be once the given tips are taken, ready to be saved.
 * Pure: the trip and its collections are copied, never mutated.
 *
 * Only the quantified tips rewrite anything — `trip_duration` and
 * `high_emission_activity` are informational and are ignored here.
 *
 * `nameAlternative` renames a leg replaced by an alternative; it takes the
 * mode the leg becomes, since the name leads with it. The wording is
 * translated, which a pure function has no business reaching for.
 */
export function applyReductionTips(
  trip: Trip,
  tips: ReductionTip[],
  modes: Record<string, AlternativeMode>,
  nameAlternative: (transport: Transport, mode: AlternativeMode) => string,
): Trip {
  const transportRewrites = new Map<string, (t: Transport) => Transport>();
  const ecoAccommodationIds = new Set<string>();

  tips.forEach((tip) => {
    switch (tip.type) {
      case 'transport_alternative': {
        const data = tip.data as TransportAlternativeTipData;
        const mode = modes[tip.id] ?? getBestAlternativeMode(data);
        transportRewrites.set(data.transportId, (transport) =>
          toAlternative(
            transport,
            data,
            mode,
            nameAlternative(transport, mode),
          ),
        );
        break;
      }
      case 'direct_flight': {
        const data = tip.data as DirectFlightTipData;
        transportRewrites.set(data.transportId, toDirectFlight);
        break;
      }
      case 'eco_accommodation': {
        const data = tip.data as EcoAccommodationTipData;
        ecoAccommodationIds.add(data.accommodationId);
        break;
      }
    }
  });

  return {
    ...trip,
    transports: trip.transports.map((transport) => {
      const rewrite = transport.id && transportRewrites.get(transport.id);
      return rewrite ? rewrite(transport) : transport;
    }),
    accommodations: trip.accommodations.map((accommodation) =>
      accommodation.id && ecoAccommodationIds.has(accommodation.id)
        ? toEcoFriendly(accommodation)
        : accommodation,
    ),
  };
}

/**
 * The alternative is priced on the distance actually travelled, so the
 * itinerary is replaced by that distance — the API's mock does the same, and
 * for trains, buses and cars the engine reads the stops before the distance.
 * `return` goes with it: `data.distance` is the total km of the leg, return
 * included.
 */
function toAlternative(
  transport: Transport,
  data: TransportAlternativeTipData,
  mode: AlternativeMode,
  title: string,
): Transport {
  return {
    ...transport,
    ...ALTERNATIVE_TRANSPORT[mode],
    // The leg no longer is what its name says — "Vol Paris → Nice" would
    // outlive the flight it describes.
    title,
    distance: data.distance,
    return: false,
    roadStops: [],
    flightStops: [],
    startAddress: undefined,
    endAddress: undefined,
    // The leg is no longer a flight: leaving the flight identity behind would
    // let the Travel Impact Model be consulted for a train.
    flightNumber: undefined,
    carrierCode: undefined,
    flightDate: undefined,
  };
}

/** Same flight without its stopovers, departure and arrival kept in order. */
function toDirectFlight(transport: Transport): Transport {
  return {
    ...transport,
    flightStops: (transport.flightStops ?? [])
      .filter((stop) => stop.type !== FlightStopType.Stopover)
      .map((stop, index) => ({ ...stop, order: index })),
  };
}

function toEcoFriendly(accommodation: Accommodation): Accommodation {
  return { ...accommodation, isEcoFriendly: true };
}
